import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { alreadyHasPhotos, claimRequest, settleRequest, unsureSaveWords } from '../client-ref.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { residentName, STATUS_LABEL } from '../livestock-journey.js';
import {
  courseProgress,
  courseTestAsReading,
  courseWarnings,
  isCopperProduct,
  testSeriesByTest,
  TEST_KINDS,
  TEST_LABEL,
  OBSERVATION_CHIPS,
} from '../../../shared/treatment.js';
import { localDateIso, localDateTime } from '../local-date.js';
import { NotePhotoPicker } from './NotePhotoPicker.jsx';
import { uploadNotePhotos, photosFailedMessage } from '../note-photos.js';
import './treatments.css';
import { isQuarantineTank } from '../../../shared/tank-purpose.js';
import { paramLabelLower } from '../../../shared/param-labels.js';
import { confirmPromptFor } from '../reading-confirm.js';

// Quarantine and treatment courses for one tank (Phase 5): what is in
// quarantine or being treated, which day it is, and the keeper's log of
// observations, doses, tests and water changes. TankSensei records; the
// dose and the target always come from the product label.

const localToday = () => localDateIso();

function longDate(v) {
  if (!v) return '';
  const dayOnly = String(v).length === 10;
  const d = new Date(dayOnly ? `${v}T12:00:00Z` : v);
  if (Number.isNaN(d.getTime())) return '';
  // A date-only value is a calendar day: read in UTC, or a keeper at
  // UTC+13 sees the next day.
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(dayOnly ? { timeZone: 'UTC' } : {}),
  });
}

function when(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const KIND_LABEL = { quarantine: 'Quarantine', treatment: 'Treatment' };
const ENTRY_LABEL = {
  observation: 'Observation',
  dose: 'Dose',
  test: 'Test',
  water_change: 'Water change',
};

function entryLine(e) {
  const amount =
    e.amount != null ? `${Number(e.amount).toString()}${e.unit ? ` ${e.unit}` : ''}` : '';
  const test = e.kind === 'test' && e.test ? TEST_LABEL[e.test] : '';
  return [test, amount, e.note].filter(Boolean).join(' · ');
}

// A resident as the course card names it: where it is now when that is
// no longer this course's tank, or what happened to it.
export function residentLine(r, course) {
  if (r.status && r.status !== 'in_tank') {
    return `${residentName(r)} (${(STATUS_LABEL[r.status] || r.status).toLowerCase()})`;
  }
  if (r.tank_id && course?.tank_id && r.tank_id !== course.tank_id) {
    return r.tank_name
      ? `${residentName(r)} (moved to ${r.tank_name})`
      : `${residentName(r)} (moved)`;
  }
  return residentName(r);
}

function hasReefLife(residents) {
  return residents.some((r) => r.kind === 'coral' || r.kind === 'invert');
}

export function TreatmentsView({ tank, onClose, onReload, backLabel = 'Home' }) {
  const confirm = useConfirm();
  const [courses, setCourses] = useState(null);
  const [residents, setResidents] = useState([]);
  const [err, setErr] = useState('');
  const [starting, setStarting] = useState(null);
  const [logging, setLogging] = useState(null);
  const [finishing, setFinishing] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const canWrite = !tank?.role || tank.role === 'owner' || tank.role === 'editor';
  const isOwner = !tank?.role || tank.role === 'owner';
  const seq = useRef(0);

  const load = async () => {
    const mine = ++seq.current;
    setErr('');
    try {
      const out = await api(`/api/tanks/${tank.id}/treatments`);
      if (mine === seq.current) setCourses(out?.courses || []);
    } catch (e) {
      if (mine !== seq.current) return;
      setErr(e.message || 'Could not load.');
      setCourses([]);
    }
  };
  useEffect(() => {
    load();
    api(`/api/tanks/${tank.id}/livestock`)
      .then((list) => setResidents(Array.isArray(list) ? list : []))
      .catch(() => setResidents([]));
    return () => {
      seq.current += 1;
    };
  }, [tank.id]);

  const today = localToday();
  const reefLife = hasReefLife(residents);
  const { active, past } = useMemo(
    () => ({
      active: (courses || []).filter((c) => c.status === 'active'),
      past: (courses || []).filter((c) => c.status !== 'active'),
    }),
    [courses]
  );

  const removeCourse = async (c) => {
    const ok = await confirm({
      title: `Delete "${c.title}"?`,
      body: 'Only for one started by mistake. Its log is deleted too. To end it and keep the record, use Finish.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api(`/api/tanks/${tank.id}/treatments/${c.id}`, { method: 'DELETE' });
      await load();
      await onReload?.();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
    }
  };

  const removeEntry = async (c, e) => {
    const ok = await confirm({
      title: 'Delete this entry?',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api(`/api/tanks/${tank.id}/treatments/${c.id}/entries/${e.id}`, { method: 'DELETE' });
      await load();
    } catch (x) {
      setErr(x.message || 'Could not delete.');
    }
  };

  return (
    <div className="lb-page" aria-labelledby="tr-title">
      <header className="lb-page-header">
        <button
          type="button"
          aria-label={`Back to ${backLabel.toLowerCase()}`}
          className="ts-back"
          onClick={onClose}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>{backLabel}</span>
        </button>
        <div className="ts-page-title">
          <div className="lb-eyebrow">{tank.name}</div>
          <h2 id="tr-title" className="font-heading lb-title">
            Quarantine &amp; treatment
          </h2>
        </div>
      </header>

      <div className="lb-page-body">
        <p className="tr-lede">
          Keep a record of new arrivals in quarantine and of any treatment: what you used, when, and
          what you saw. Use the product label for doses and targets; TankSensei keeps the log and
          reminds you daily.
        </p>

        {canWrite && (
          <div className="tr-start">
            <button
              type="button"
              className="tr-btn tr-btn-primary font-heading"
              onClick={() => setStarting('quarantine')}
            >
              Start quarantine
            </button>
            <button
              type="button"
              className="tr-btn font-heading"
              onClick={() => setStarting('treatment')}
            >
              Start treatment
            </button>
          </div>
        )}

        {err && (
          <p className="hv-resident-err" role="alert">
            {err}
          </p>
        )}

        {courses == null ? (
          <p className="tr-empty">Loading…</p>
        ) : active.length === 0 ? (
          <p className="tr-empty">Nothing in quarantine or treatment right now.</p>
        ) : (
          <div className="tr-list">
            {active.map((c) => (
              <CourseCard
                key={c.id}
                course={c}
                tankId={tank.id}
                today={today}
                reefLife={reefLife}
                canWrite={canWrite}
                canDelete={isOwner}
                onLog={() => setLogging(c)}
                onFinish={() => setFinishing(c)}
                onDelete={() => removeCourse(c)}
                onDeleteEntry={(e) => removeEntry(c, e)}
              />
            ))}
          </div>
        )}

        {past.length > 0 && (
          <div className="tr-past">
            <button
              type="button"
              className="tr-past-toggle font-heading"
              aria-expanded={showPast}
              onClick={() => setShowPast((v) => !v)}
            >
              {showPast ? 'Hide' : 'Show'} past ({past.length})
            </button>
            {showPast && (
              <div className="tr-list">
                {past.map((c) => (
                  <CourseCard
                    key={c.id}
                    course={c}
                    tankId={tank.id}
                    today={today}
                    reefLife={reefLife}
                    canWrite={canWrite}
                    canDelete={isOwner}
                    onDelete={() => removeCourse(c)}
                    onDeleteEntry={(e) => removeEntry(c, e)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {starting && (
        <StartCourseSheet
          tank={tank}
          kind={starting}
          residents={residents.filter((r) => !r.status || r.status === 'in_tank')}
          reefLife={reefLife}
          onClose={() => setStarting(null)}
          onDone={async () => {
            await load();
            await onReload?.();
          }}
        />
      )}
      {logging && (
        <EntrySheet
          tank={tank}
          course={logging}
          onClose={() => setLogging(null)}
          onDone={async () => {
            await load();
            // Home's Today shows the day's check, which the entry ticks.
            await onReload?.();
          }}
          onReading={onReload}
        />
      )}
      {finishing && (
        <FinishSheet
          tank={tank}
          course={finishing}
          onClose={() => setFinishing(null)}
          onDone={async () => {
            await load();
            await onReload?.();
          }}
        />
      )}
    </div>
  );
}

function CourseCard({
  course: c,
  tankId,
  today,
  reefLife,
  canWrite,
  canDelete,
  onLog,
  onFinish,
  onDelete,
  onDeleteEntry,
}) {
  const [open, setOpen] = useState(c.status === 'active');
  const prog = courseProgress(c, today);
  const warnings = c.status === 'active' ? courseWarnings(c, { reefLife }) : [];
  const pct = prog?.planned ? Math.min(100, Math.round((prog.day / prog.planned) * 100)) : null;
  const ended = c.status !== 'active';
  return (
    <article className={`tr-card${ended ? ' is-ended' : ''}`}>
      <div className="tr-card-head">
        <div>
          <div className="tr-kind font-heading">
            {KIND_LABEL[c.kind]}
            {c.location === 'display' ? ' · in this tank' : ' · in another tank'}
          </div>
          <h3 className="tr-title">{c.title}</h3>
        </div>
        {prog && (
          <div className="tr-day font-mono" aria-label="Progress">
            {ended
              ? c.status === 'completed'
                ? `Finished, ${prog.day} ${prog.day === 1 ? 'day' : 'days'}`
                : `Stopped on day ${prog.day}`
              : prog.planned
                ? `Day ${prog.day} of ${prog.planned}`
                : `Day ${prog.day}`}
          </div>
        )}
      </div>
      {pct != null && !ended && (
        <div
          className="tr-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
      )}
      {prog?.due && !ended && (
        <p className="tr-note">
          The planned {prog.planned} days are up. Finish it when you are ready.
        </p>
      )}

      <dl className="tr-facts">
        <div>
          <dt>Started</dt>
          <dd>{longDate(c.started_on)}</dd>
        </div>
        {c.ended_on && (
          <div>
            <dt>Ended</dt>
            <dd>{longDate(c.ended_on)}</dd>
          </div>
        )}
        {c.reason && (
          <div>
            <dt>Why</dt>
            <dd>{c.reason}</dd>
          </div>
        )}
        {c.product && (
          <div>
            <dt>Product</dt>
            <dd>{c.product}</dd>
          </div>
        )}
        {c.label_target && (
          <div>
            <dt>Label target</dt>
            <dd>{c.label_target}</dd>
          </div>
        )}
        {c.residents?.length > 0 && (
          <div>
            <dt>Residents</dt>
            <dd>{c.residents.map((r) => residentLine(r, c)).join(', ')}</dd>
          </div>
        )}
        {c.outcome && (
          <div>
            <dt>Outcome</dt>
            <dd>{c.outcome}</dd>
          </div>
        )}
      </dl>

      {warnings.map((w) => (
        <p key={w} className="tr-warn" role="note">
          {w}
        </p>
      ))}

      {testSeriesByTest(c.entries, { copperCourse: isCopperProduct(c.product) }).map((series) => (
        <TestChart
          key={series.test}
          series={series}
          target={series.test === 'copper' && isCopperProduct(c.product) ? c.label_target : null}
        />
      ))}

      {canWrite && !ended && (
        <div className="tr-actions">
          <button type="button" className="tr-btn tr-btn-primary font-heading" onClick={onLog}>
            Add entry
          </button>
          <button type="button" className="tr-btn font-heading" onClick={onFinish}>
            Finish
          </button>
        </div>
      )}

      <button
        type="button"
        className="tr-log-toggle font-heading"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Log ({c.entries?.length || 0})
      </button>
      {open && (
        <ul className="tr-entries">
          {(c.entries || []).length === 0 && <li className="tr-empty">Nothing logged yet.</li>}
          {(c.entries || []).map((e) => (
            <li key={e.id} className="tr-entry">
              <div>
                <span className="tr-entry-kind font-heading">{ENTRY_LABEL[e.kind] || e.kind}</span>
                <span className="tr-entry-when font-mono">{when(e.ts)}</span>
              </div>
              {entryLine(e) && <div className="tr-entry-text">{entryLine(e)}</div>}
              {e.image_ids?.length > 0 && (
                <span className="hv-journal-photos">
                  {e.image_ids.map((id) => {
                    const src = `/api/tanks/${tankId}/treatments/${c.id}/entries/${e.id}/images/${id}`;
                    return (
                      <a key={id} href={src} target="_blank" rel="noreferrer">
                        <img src={src} alt="" loading="lazy" />
                      </a>
                    );
                  })}
                </span>
              )}
              {canWrite && (!ended || canDelete) && (
                <button
                  type="button"
                  className="tr-entry-del"
                  aria-label="Delete entry"
                  onClick={() => onDeleteEntry(e)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canDelete && (
        <button type="button" className="rj-mistake" onClick={onDelete}>
          {ended ? 'Delete this record and its log' : 'Started by mistake? Delete it'}
        </button>
      )}
    </article>
  );
}

function shortDay(ts) {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// One test's logged results over time (copper, ammonia...): one chart per
// test, never two tests on one line. One axis, the app's accent colour;
// the label target stays as text beside the copper chart because it is the
// label's wording, not a number we read. Each point has a hover and a tap
// title, and the log below is the table view.
function TestChart({ series, target }) {
  const [hover, setHover] = useState(null);
  if (!series) return null;
  const { unit, points, label } = series;
  const W = 320;
  const H = 120;
  const pad = { l: 36, r: 12, t: 12, b: 22 };
  const vals = points.map((p) => p.value);
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  if (lo === hi) {
    lo = lo - 1 < 0 ? 0 : lo - 1;
    hi += 1;
  }
  const t0 = points[0].ts;
  const t1 = points[points.length - 1].ts;
  const x = (t) => pad.l + ((t - t0) / (t1 - t0 || 1)) * (W - pad.l - pad.r);
  const y = (v) => pad.t + (1 - (v - lo) / (hi - lo)) * (H - pad.t - pad.b);
  const fmt = (v) => Number(v.toFixed(3)).toString();
  const u = unit ? ` ${unit}` : '';
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.ts)},${y(p.value)}`).join(' ');
  const last = points[points.length - 1];
  const shown = hover != null ? points[hover] : last;
  return (
    <figure className="tr-chart">
      <figcaption className="tr-chart-head">
        <span className="tr-chart-title font-heading">{label}</span>
        <span className="tr-chart-value font-mono">
          {fmt(shown.value)}
          {u} · {shortDay(shown.ts)}
        </span>
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="tr-chart-svg"
        role="img"
        aria-label={`${label}: ${points.map((p) => `${fmt(p.value)}${u} on ${shortDay(p.ts)}`).join(', ')}`}
        onMouseLeave={() => setHover(null)}
      >
        <line className="tr-chart-axis" x1={pad.l} x2={W - pad.r} y1={H - pad.b} y2={H - pad.b} />
        <text className="tr-chart-tick" x={pad.l - 6} y={y(hi) + 4} textAnchor="end">
          {fmt(hi)}
        </text>
        <text className="tr-chart-tick" x={pad.l - 6} y={y(lo) + 4} textAnchor="end">
          {fmt(lo)}
        </text>
        <text className="tr-chart-tick" x={pad.l} y={H - 6}>
          {shortDay(t0)}
        </text>
        <text className="tr-chart-tick" x={W - pad.r} y={H - 6} textAnchor="end">
          {shortDay(t1)}
        </text>
        <path className="tr-chart-line" d={path} />
        {points.map((p, i) => (
          <g key={p.ts + ':' + i}>
            <circle
              className={`tr-chart-dot${hover === i ? ' is-on' : ''}`}
              cx={x(p.ts)}
              cy={y(p.value)}
              r={4}
            />
            <circle
              className="tr-chart-hit"
              cx={x(p.ts)}
              cy={y(p.value)}
              r={14}
              onMouseEnter={() => setHover(i)}
              onClick={() => setHover(i)}
            >
              <title>{`${fmt(p.value)}${u}, ${shortDay(p.ts)}`}</title>
            </circle>
          </g>
        ))}
      </svg>
      {target && <div className="tr-chart-target">Label target: {target}</div>}
    </figure>
  );
}

function SheetFrame({ eyebrow, title, busy, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  return (
    <div
      className="hv-resident-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="hv-resident-sheet">
        <header className="hv-resident-sheet-head">
          <div>
            <div className="hv-resident-sheet-eyebrow font-heading">{eyebrow}</div>
            <h3 className="hv-resident-sheet-title">{title}</h3>
          </div>
          <button
            type="button"
            className="hv-resident-sheet-close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function SheetActions({ busy, onClose, onSave, saveLabel = 'Save' }) {
  return (
    <div className="hv-resident-actions">
      <button
        type="button"
        className="hv-resident-action font-heading"
        onClick={onClose}
        disabled={busy}
      >
        Cancel
      </button>
      <button
        type="button"
        className="hv-resident-action save font-heading"
        onClick={onSave}
        disabled={busy}
      >
        {busy ? '…' : saveLabel}
      </button>
    </div>
  );
}

function StartCourseSheet({ tank, kind, residents, reefLife, onClose, onDone }) {
  const quarantine = kind === 'quarantine';
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [product, setProduct] = useState('');
  const [labelTarget, setLabelTarget] = useState('');
  // A course started in a quarantine tank happens in that tank.
  const [location, setLocation] = useState(isQuarantineTank(tank) ? 'display' : 'separate');
  const [startedOn, setStartedOn] = useState(localToday);
  const [plannedDays, setPlannedDays] = useState('');
  const [picked, setPicked] = useState([]);
  const [remind, setRemind] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const warnings = courseWarnings({ product, location }, { reefLife });
  const toggle = (id) =>
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/treatments`, {
        method: 'POST',
        body: {
          kind,
          title,
          reason,
          product,
          label_target: labelTarget,
          location,
          started_on: startedOn,
          planned_days: plannedDays,
          livestock_ids: picked,
          remind_daily: remind,
        },
      });
      await onDone?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not save.');
      setBusy(false);
    }
  };

  return (
    <SheetFrame
      eyebrow={quarantine ? 'Start quarantine' : 'Start treatment'}
      title={quarantine ? 'New quarantine' : 'New treatment'}
      busy={busy}
      onClose={onClose}
    >
      <label className="rj-field">
        <span className="rj-label font-heading">Name</span>
        <input
          type="text"
          maxLength={80}
          placeholder={quarantine ? 'Yellow tang, new arrival' : 'Ich on the tang'}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={busy}
        />
      </label>

      {!quarantine && (
        <label className="rj-field">
          <span className="rj-label font-heading">What are you treating? (optional)</span>
          <input
            type="text"
            maxLength={500}
            placeholder="White spots on the fins, scratching"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          />
        </label>
      )}

      <label className="rj-field">
        <span className="rj-label font-heading">
          {quarantine ? 'Medication, if any (optional)' : 'Product (optional)'}
        </span>
        <input
          type="text"
          maxLength={120}
          placeholder="The name on the bottle"
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          disabled={busy}
        />
      </label>

      {(product.trim() || !quarantine) && (
        <label className="rj-field">
          <span className="rj-label font-heading">Target from the label (optional)</span>
          <input
            type="text"
            maxLength={200}
            placeholder="As printed on the label"
            value={labelTarget}
            onChange={(e) => setLabelTarget(e.target.value)}
            disabled={busy}
          />
        </label>
      )}

      <div className="rj-label font-heading">Where</div>
      <div className="rj-choices" role="radiogroup" aria-label="Where">
        {[
          {
            k: 'separate',
            label: 'Another tank',
            hint: 'A quarantine or hospital tank you have not added here',
          },
          { k: 'display', label: `In ${tank.name}`, hint: 'This tank is where it happens' },
        ].map((c) => (
          <button
            key={c.k}
            type="button"
            role="radio"
            aria-checked={location === c.k}
            className={`rj-choice${location === c.k ? ' is-on' : ''}`}
            onClick={() => setLocation(c.k)}
            disabled={busy}
          >
            <span className="rj-choice-label">{c.label}</span>
            <span className="rj-choice-hint">{c.hint}</span>
          </button>
        ))}
      </div>

      {warnings.map((w) => (
        <p key={w} className="tr-warn" role="note">
          {w}
        </p>
      ))}
      {product.trim() && (
        <p className="tr-note">Follow the dose and schedule on the product label.</p>
      )}

      <div className="tr-row">
        <label className="rj-field">
          <span className="rj-label font-heading">Started</span>
          <input
            type="date"
            value={startedOn}
            max={localToday()}
            onChange={(e) => setStartedOn(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="rj-field">
          <span className="rj-label font-heading">Planned days (optional)</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={plannedDays}
            onChange={(e) => setPlannedDays(e.target.value)}
            disabled={busy}
          />
        </label>
      </div>

      {residents.length > 0 && (
        <>
          <div className="rj-label font-heading">Which residents? (optional)</div>
          <div className="tr-residents">
            {residents.map((r) => (
              <label key={r.id} className="tr-check">
                <input
                  type="checkbox"
                  checked={picked.includes(r.id)}
                  onChange={() => toggle(r.id)}
                  disabled={busy}
                />
                <span>{residentName(r)}</span>
              </label>
            ))}
          </div>
        </>
      )}

      <label className="tr-check">
        <input
          type="checkbox"
          checked={remind}
          onChange={(e) => setRemind(e.target.checked)}
          disabled={busy}
        />
        <span>Remind me to check in every day</span>
      </label>

      {err && (
        <p className="hv-resident-err" role="alert">
          {err}
        </p>
      )}
      <SheetActions busy={busy} onClose={onClose} onSave={save} saveLabel="Start" />
    </SheetFrame>
  );
}

const PPM_TESTS = new Set(['ammonia', 'nitrite', 'nitrate']);

// The unit a test chip fills in. `auto` is the unit the form filled itself:
// it follows the chip, so pH after copper is not "7.9 ppm" (sweep 9); a unit
// the keeper typed is theirs and stays.
export function chipUnit(test, { unit, auto }) {
  if (unit.trim() && unit !== auto) return { unit, auto };
  const next = test === 'copper' || PPM_TESTS.has(test) ? 'ppm' : '';
  return { unit: next, auto: next };
}

// The residents a finished course can move: still in the tank and in THIS
// tank. One an earlier course already moved was offered again (sweep 9).
export function residentsStillHere(course) {
  return (course?.residents || []).filter(
    (r) =>
      (!r.status || r.status === 'in_tank') &&
      (!r.tank_id || !course.tank_id || r.tank_id === course.tank_id)
  );
}

// "an ammonia reading", "a pH reading".
function readingWords(param) {
  const label = paramLabelLower(param);
  return `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label} reading`;
}

const FIT_LABEL = { caution: 'add with caution', no: 'not recommended' };

function EntrySheet({ tank, course, onClose, onDone, onReading }) {
  const [kind, setKind] = useState('observation');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('');
  const [autoUnit, setAutoUnit] = useState('');
  const [test, setTest] = useState('');
  const [note, setNote] = useState('');
  const [when, setWhen] = useState(localDateTime);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [finished, setFinished] = useState(false);
  const [alsoReading, setAlsoReading] = useState(false);
  const [askedSure, setAskedSure] = useState('');
  const needsAmount = kind === 'dose' || kind === 'test';
  const hasAmount = needsAmount || kind === 'water_change';
  // Only a test taken in THIS tank can be one of its readings: a course
  // kept for a fish in a tank not in TankSensei tested that other water.
  const asReading =
    kind === 'test' && course.location === 'display'
      ? courseTestAsReading({ test, unit, amount }, tank?.params)
      : null;
  const savingReading = alsoReading && !!asReading;

  // Each kind starts with its own amount and unit: a dose typed in mL
  // must not be saved under the copper test's ppm, and an observation
  // carries no number the keeper can no longer see.
  const pickKind = (k) => {
    setKind(k);
    setAmount('');
    const copper = k === 'test' && isCopperProduct(course.product);
    setUnit(copper ? 'ppm' : '');
    setAutoUnit(copper ? 'ppm' : '');
    setTest(copper ? 'copper' : '');
  };

  const save = async () => {
    // Each test charts on its own line, so it has to say what it measured.
    if (kind === 'test' && !test) {
      setErr('Choose what you tested.');
      return;
    }
    // The same "Are you sure?" a reading typed on Home gets; saving again
    // with it showing means yes.
    if (savingReading) {
      const salinitySg = asReading.param === 'salinity' && asReading.value < 2;
      const sure = salinitySg ? null : confirmPromptFor(tank, asReading.param, asReading.value);
      if (sure && askedSure !== sure) {
        setAskedSure(sure);
        setErr(sure);
        return;
      }
    }
    setBusy(true);
    setErr('');
    let posted = false;
    try {
      const base = `/api/tanks/${tank.id}/treatments/${course.id}/entries`;
      const ts = when ? new Date(when).toISOString() : undefined;
      const fields = {
        kind,
        amount: hasAmount && amount !== '' ? amount : null,
        unit: hasAmount ? unit : null,
        test: kind === 'test' ? test : null,
        note,
        ts,
        with_photos: photos.length > 0,
      };
      const scope = `course-entry:${course.id}`;
      const entry = await api(base, {
        method: 'POST',
        body: {
          ...fields,
          client_ref: claimRequest(scope, JSON.stringify([fields, photos.length])),
        },
      });
      posted = true;
      settleRequest(scope);
      // Opt-in: the result also goes to the tank's readings, through the
      // readings route like any reading, so its checks and alerts apply.
      let readingErr = '';
      if (savingReading) {
        try {
          await api(`/api/tanks/${tank.id}/readings`, {
            method: 'POST',
            body: {
              param: asReading.param,
              value: asReading.value,
              unit: asReading.unit,
              method: 'manual',
              note: `From the log of ${course.title}`.slice(0, 300),
              ts,
            },
          });
          await onReading?.();
        } catch (x) {
          readingErr = `Saved in the log, but not as ${readingWords(asReading.param)}: ${x.message || 'it could not be saved.'}`;
        }
      }
      if (readingErr) {
        setErr(readingErr);
        setFinished(true);
      }
      const photoErrors = [];
      // The same entry again (its reply was lost): its photos went already.
      const failed =
        photos.length && !alreadyHasPhotos(entry, photos.length)
          ? await uploadNotePhotos(`${base}/${entry.id}/images`, photos, photoErrors)
          : 0;
      if (failed && failed === photos.length && kind === 'observation' && !note.trim()) {
        // A photo-only entry whose photos all failed is an empty entry.
        await api(`${base}/${entry.id}`, { method: 'DELETE' }).catch(() => {});
        setErr(photoErrors[0] || 'The photo could not be uploaded. Try again.');
        setBusy(false);
        return;
      }
      if (failed) {
        setErr(
          [readingErr, photosFailedMessage(failed, photos.length, photoErrors[0])]
            .filter(Boolean)
            .join(' ')
        );
        setPhotos([]);
        setFinished(true);
        setBusy(false);
        return;
      }
      if (readingErr) {
        setBusy(false);
        return;
      }
      await onDone?.();
      onClose();
    } catch (e) {
      setErr((!posted && unsureSaveWords(e)) || e.message || 'Could not save.');
      setBusy(false);
    }
  };

  return (
    <SheetFrame eyebrow={course.title} title="Log an entry" busy={busy} onClose={onClose}>
      <div className="rj-choices" role="radiogroup" aria-label="What are you logging">
        {Object.entries(ENTRY_LABEL).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            className={`rj-choice${kind === k ? ' is-on' : ''}`}
            onClick={() => pickKind(k)}
            disabled={busy}
          >
            <span className="rj-choice-label">{label}</span>
          </button>
        ))}
      </div>

      {kind === 'test' && (
        <>
          <div className="rj-label font-heading">What did you test?</div>
          <div className="rj-choices" role="radiogroup" aria-label="What did you test">
            {TEST_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={test === k}
                className={`rj-choice${test === k ? ' is-on' : ''}`}
                onClick={() => {
                  setTest(k);
                  const next = chipUnit(k, { unit, auto: autoUnit });
                  setUnit(next.unit);
                  setAutoUnit(next.auto);
                }}
                disabled={busy}
              >
                <span className="rj-choice-label">{TEST_LABEL[k]}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {hasAmount && (
        <div className="tr-row">
          <label className="rj-field">
            <span className="rj-label font-heading">
              {kind === 'dose' ? 'Amount' : kind === 'test' ? 'Result' : 'How much (optional)'}
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="rj-field">
            <span className="rj-label font-heading">Unit</span>
            <input
              type="text"
              maxLength={20}
              placeholder={kind === 'dose' ? 'mL' : kind === 'test' ? 'ppm' : '%'}
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>
      )}

      {asReading && (
        <label className="tr-check">
          <input
            type="checkbox"
            checked={alsoReading}
            onChange={(e) => {
              setAlsoReading(e.target.checked);
              setAskedSure('');
            }}
            disabled={busy || finished}
          />
          <span>
            Also save it as {readingWords(asReading.param)} for {tank.name}. It counts like any
            reading you log: it shows on the card and can alert.
          </span>
        </label>
      )}

      <label className="rj-field">
        <span className="rj-label font-heading">When</span>
        <input
          type="datetime-local"
          value={when}
          max={localDateTime()}
          onChange={(e) => setWhen(e.target.value)}
          disabled={busy}
        />
      </label>

      {kind === 'observation' && (
        <div className="tr-chips" aria-label="Quick words">
          {OBSERVATION_CHIPS.map((w) => (
            <button
              key={w}
              type="button"
              className="tr-chip"
              disabled={busy || finished}
              onClick={() =>
                setNote((n) =>
                  n.trim() ? `${n.trim().replace(/[.,]$/, '')}, ${w.toLowerCase()}` : w
                )
              }
            >
              {w}
            </button>
          ))}
        </div>
      )}
      <label className="rj-field">
        <span className="rj-label font-heading">
          {kind === 'observation' ? 'What did you see?' : 'Note (optional)'}
        </span>
        <textarea
          rows={3}
          maxLength={500}
          placeholder={
            kind === 'observation'
              ? 'Eating well, no spots, breathing normally'
              : kind === 'test'
                ? 'Which test kit'
                : ''
          }
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
        />
      </label>

      <div className="rj-label font-heading">Photos (optional)</div>
      <NotePhotoPicker files={photos} onChange={setPhotos} disabled={busy || finished} />

      {err && (
        <p className="hv-resident-err" role="alert">
          {err}
        </p>
      )}
      {finished ? (
        <div className="hv-resident-actions">
          <button
            type="button"
            className="hv-resident-action save font-heading"
            onClick={async () => {
              await onDone?.();
              onClose();
            }}
          >
            Close
          </button>
        </div>
      ) : (
        <SheetActions busy={busy} onClose={onClose} onSave={save} />
      )}
    </SheetFrame>
  );
}

function FinishSheet({ tank, course, onClose, onDone }) {
  const [status, setStatus] = useState('completed');
  const [endedOn, setEndedOn] = useState(localToday);
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [moveTo, setMoveTo] = useState('');
  const [otherTanks, setOtherTanks] = useState([]);
  const quarantine = course.kind === 'quarantine';
  const inTank = residentsStillHere(course);

  // Where the residents can go when quarantine ends: the owner's other
  // tanks this keeper can write to (the server checks the same).
  useEffect(() => {
    if (!inTank.length) return undefined;
    let live = true;
    api('/api/tanks')
      .then((list) => {
        if (!live || !Array.isArray(list)) return;
        setOtherTanks(
          list.filter(
            (t) =>
              t.id !== tank.id &&
              t.user_id === tank.user_id &&
              (t.role === 'owner' || t.role === 'editor')
          )
        );
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [tank.id]);

  // Before a move, the destination's own fit verdict for each species, the
  // same one Browse shows for that tank (a second tang into a display the
  // rules call "no" went through with no word).
  const [fits, setFits] = useState([]);
  const moving = status === 'completed' && moveTo ? moveTo : '';
  useEffect(() => {
    setFits([]);
    if (!moving) return undefined;
    let live = true;
    const withSpecies = inTank.filter((r) => r.catalog_id != null);
    Promise.all(
      withSpecies.map((r) =>
        api(`/api/tanks/${moving}/livestock-catalog/${r.catalog_id}`)
          .then((d) => ({ r, fit: d?.fit || null }))
          .catch(() => ({ r, fit: null }))
      )
    ).then((list) => {
      if (live) setFits(list.filter((x) => x.fit && x.fit.verdict !== 'good'));
    });
    return () => {
      live = false;
    };
  }, [moving]);
  const movingName = otherTanks.find((t) => t.id === moving)?.name || '';

  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/treatments/${course.id}/finish`, {
        method: 'POST',
        body: {
          status,
          ended_on: endedOn,
          outcome,
          move_to_tank_id: status === 'completed' && moveTo ? moveTo : null,
        },
      });
      await onDone?.(moveTo && status === 'completed');
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not save.');
      setBusy(false);
    }
  };

  return (
    <SheetFrame eyebrow={course.title} title="Finish" busy={busy} onClose={onClose}>
      <div className="rj-choices" role="radiogroup" aria-label="How did it end">
        {[
          {
            k: 'completed',
            label: 'Done',
            hint: quarantine ? 'Quarantine finished' : 'Treatment finished',
          },
          { k: 'stopped', label: 'Stopped early', hint: 'Ended before the plan' },
        ].map((c) => (
          <button
            key={c.k}
            type="button"
            role="radio"
            aria-checked={status === c.k}
            className={`rj-choice${status === c.k ? ' is-on' : ''}`}
            onClick={() => setStatus(c.k)}
            disabled={busy}
          >
            <span className="rj-choice-label">{c.label}</span>
            <span className="rj-choice-hint">{c.hint}</span>
          </button>
        ))}
      </div>
      <label className="rj-field">
        <span className="rj-label font-heading">When</span>
        <input
          type="date"
          value={endedOn}
          max={localToday()}
          onChange={(e) => setEndedOn(e.target.value)}
          disabled={busy}
        />
      </label>
      {status === 'completed' && otherTanks.length > 0 && (
        <label className="rj-field">
          <span className="rj-label font-heading">
            Move {inTank.length === 1 ? inTank[0].common_name : `the ${inTank.length} residents`} to
            another tank? (optional)
          </span>
          <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} disabled={busy}>
            <option value="">Keep in {tank.name}</option>
            {otherTanks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {fits.map(({ r, fit }) => (
        <div key={r.id} className="tr-warn tr-fit" role="note">
          <strong>
            {residentName(r)} in {movingName}: {FIT_LABEL[fit.verdict] || fit.verdict}.
          </strong>
          {(fit.reasons || [])
            .filter((x) => x.level === 'no' || x.level === 'caution')
            .map((x) => (
              <span key={x.code || x.message}> {x.message}</span>
            ))}
        </div>
      ))}
      <label className="rj-field">
        <span className="rj-label font-heading">How did it go? (optional)</span>
        <textarea
          rows={3}
          maxLength={500}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          disabled={busy}
        />
      </label>
      {err && (
        <p className="hv-resident-err" role="alert">
          {err}
        </p>
      )}
      <SheetActions busy={busy} onClose={onClose} onSave={save} saveLabel="Finish" />
    </SheetFrame>
  );
}
